import { Component, OnInit, Type } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { BeltCircumferenceRangeComponent } from './belt-circumference-range.component';

export interface BeltCircumferenceRangeProps extends FormlyFieldProps {
  ranges: { min: number; max: number }[];
}

export interface FormlyBeltCircumferenceRangeFieldConfig extends FormlyFieldConfig<BeltCircumferenceRangeProps> {
  type: 'belt-circumference-range' | Type<FormlyBeltCircumferenceRangeComponent>;
}

@Component({
  imports: [ReactiveFormsModule, BeltCircumferenceRangeComponent],
  selector: 'vectronic-formly-belt-circumference-range-wrapper',
  template: `<vectronic-formly-belt-circumference-range
    [formControl]="formControl"
    [id]="id"
    [ranges]="props.ranges"
    [required]="props.required"
    [adminView]="isAdmin"
  ></vectronic-formly-belt-circumference-range>`,
})
export class FormlyBeltCircumferenceRangeComponent
  extends FieldType<FieldTypeConfig<BeltCircumferenceRangeProps>>
  implements OnInit
{
  ngOnInit(): void {
    this.formControl.addValidators([this.customRangeValidator, this.customRangeConfirmedValidator]);
  }
  get isAdmin() {
    return !!this.options.formState['isAdmin'];
  }

  private customRangeValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const value = control.getRawValue();
    if (!value || value.type !== 'custom') return null;
    if (value.min == null || value.max == null) {
      return { customRangeRequired: { message: 'Custom circumference is missing' } };
    }
    const minFrom = this.props.min ?? 1;
    if (value.min != null && value.min < minFrom)
      return {
        minCircumference: { message: `Minimum circumference must be greater than ${minFrom - 1}` },
      };
    const minTo = value.min ?? 1;
    if (value.max != null && value.max <= minTo)
      return {
        maxCircumference: { message: `Maximum circumference must be greater than ${minTo}` },
      };
    if (value.min >= value.max) {
      return {
        minMaxCircumference: {
          message: 'Maximum circumference must be greater than minimum circumference',
        },
      };
    }
    return null;
  };

  private customRangeConfirmedValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const value = control.getRawValue();
    if (!value || !this.isAdmin) return null;
    const isCustom = value.type === 'custom';
    if (!isCustom) return null;
    if (!value.confirmed)
      return { customRangeNotConfirmed: { message: 'Custom circumference is not confirmed' } };
  };
}
